/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect, useState } from 'react';
import { SUPPORTED_LOCALES, SupportedLocales } from '@airasia-rn/ui';

const DEFAULT_LOCALE: SupportedLocales = 'en-gb';
const UHF_LOCALE_CHANGE_EVENT = 'uhfLocaleChange';

const isSupportedLocale = (locale: unknown): locale is SupportedLocales =>
  typeof locale === 'string' &&
  SUPPORTED_LOCALES.includes(locale as SupportedLocales);

export const normalizeUhfLocale = (
  locale: unknown,
): SupportedLocales | undefined => {
  if (typeof locale !== 'string') {
    return undefined;
  }

  const normalizedLocale = locale.trim().replace('_', '-').toLowerCase();
  return isSupportedLocale(normalizedLocale) ? normalizedLocale : undefined;
};

export const useSyncedUhfLocale = (initialLocale?: string) => {
  const [locale, setLocale] = useState<SupportedLocales>(
    () => normalizeUhfLocale(initialLocale) ?? DEFAULT_LOCALE,
  );

  useEffect(() => {
    const normalizedLocale =
      normalizeUhfLocale(initialLocale) ?? DEFAULT_LOCALE;

    setLocale((currentLocale) =>
      currentLocale === normalizedLocale ? currentLocale : normalizedLocale,
    );
  }, [initialLocale]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleLocaleEvent = (event: Event) => {
      const nextLocale = normalizeUhfLocale((event as CustomEvent).detail);
      if (!nextLocale) return;

      setLocale((currentLocale) =>
        currentLocale === nextLocale ? currentLocale : nextLocale,
      );
    };

    window.addEventListener(UHF_LOCALE_CHANGE_EVENT, handleLocaleEvent);

    return () => {
      window.removeEventListener(UHF_LOCALE_CHANGE_EVENT, handleLocaleEvent);
    };
  }, []);

  return locale;
};
